import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Card, Upload, message, Space, Typography, Popconfirm, Badge, Avatar, Flex, Grid } from 'antd';
import { UploadOutlined, DeleteOutlined, FileTextOutlined, HistoryOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getHistoricalImports, createHistoricalImport, deleteHistoricalImport } from '../../ChannelsServices';
import { uploadFile } from '../../../Messages/MessagesServices';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import AppTable from '../../../Shared/AppTable/AppTable';

const { Text } = Typography;
const { useBreakpoint } = Grid;

/**
 * HistoricalImportsTab component displays a paginated table of historical imports for a channel.
 * Uses AppTable for responsive display and consistent styling with other tabs.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.channel - The channel object containing channel details.
 * @returns {JSX.Element} The rendered HistoricalImportsTab component.
 */
const HistoricalImportsTab = ({ channel }) => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const screens = useBreakpoint() || {};

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [uploading, setUploading] = useState(false);

    const fetchImports = useCallback(async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const params = {
                page,
                page_size: pageSize,
                sort_by: 'created_at',
                sort_direction: 'desc'
            };
            const response = await getHistoricalImports(channel.id, params);
            const items = Array.isArray(response) ? response : (Array.isArray(response?.items) ? response.items : []);
            setData(items);
            setPagination({
                current: response.page || page,
                pageSize: response.page_size || pageSize,
                total: response.total || items.length
            });
        } catch (error) {
            console.error('Error fetching imports:', error);
            message.error(t('failed_to_load_imports'));
        } finally {
            setLoading(false);
        }
    }, [channel.id, t]);

    useEffect(() => {
        if (channel?.id) {
            fetchImports(pagination.current, pagination.pageSize);
        }
    }, [channel?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTableChange = useCallback((newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
        fetchImports(newPagination.current, newPagination.pageSize);
    }, [fetchImports]);

    const handleUpload = useCallback(async (file) => {
        setUploading(true);
        try {
            const uploadResponse = await uploadFile(file);
            const fileUploadId = uploadResponse?.items?.[0]?.id || uploadResponse?.id || uploadResponse?.data?.id;

            if (!fileUploadId) {
                throw new Error('No file ID received from upload');
            }

            await createHistoricalImport(channel.id, { file_upload_id: fileUploadId });

            message.success(t('import_started_successfully'));
            fetchImports(1, pagination.pageSize);
        } catch (error) {
            console.error('Import error:', error);
            const errorMessage = error?.response?.data?.message || t('import_failed');
            message.error(errorMessage);
        } finally {
            setUploading(false);
        }
    }, [channel.id, t, pagination.pageSize, fetchImports]);

    const handleDelete = useCallback(async (id) => {
        try {
            await deleteHistoricalImport(id);
            message.success(t('import_deleted_successfully'));
            fetchImports(pagination.current, pagination.pageSize);
        } catch (error) {
            console.error('Delete error:', error);
            message.error(t('failed_to_delete_import'));
        }
    }, [t, pagination.current, pagination.pageSize, fetchImports]);

    const avatarStyle = useMemo(() => ({
        backgroundColor: isDarkMode ? '#1f1f1f' : '#f0f0f0',
        color: isDarkMode ? '#8c8c8c' : '#595959'
    }), [isDarkMode]);

    const getStatusBadge = useCallback((status) => {
        const statusMap = {
            completed: { color: 'success', text: t('completed') },
            failed: { color: 'error', text: t('failed') },
            pending: { color: 'processing', text: t('pending') },
            in_progress: { color: 'processing', text: t('in_progress') },
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Badge status={config.color} text={config.text} />;
    }, [t]);

    const columns = useMemo(() => [
        {
            title: t('file_name'),
            key: 'file_name',
            render: (_, record) => (
                <Space>
                    <Avatar
                        icon={<FileTextOutlined />}
                        style={avatarStyle}
                    />
                    <Text>{record.file_name || record.id}</Text>
                </Space>
            ),
        },
        {
            title: t('progress'),
            dataIndex: 'progress',
            key: 'progress',
            render: (progress) => progress != null ? `${Math.round(progress)}%` : '-',
            width: 100,
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: getStatusBadge,
            width: 120,
        },
        {
            title: t('created_at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => date ? (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {format(new Date(date), 'PPP p')}
                </span>
            ) : '-',
            responsive: ['md'],
        },
        {
            title: t('actions'),
            key: 'actions',
            fixed: 'right',
            width: 80,
            render: (_, record) => (
                <Popconfirm
                    title={t('delete_import_confirmation')}
                    onConfirm={() => handleDelete(record.id)}
                    okText={t('yes')}
                    cancelText={t('no')}
                >
                    <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                </Popconfirm>
            ),
        },
    ], [t, avatarStyle, handleDelete, getStatusBadge]);

    const downloadTemplate = useCallback(() => {
        // Use semicolons as delimiter for Excel compatibility and add UTF-8 BOM
        const headers = ['first_name', 'last_name', 'email', 'phone', 'locale', 'note'];
        const BOM = '\uFEFF';
        const csvContent = BOM + headers.join(';');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'contacts_import_template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, []);

    const uploadProps = useMemo(() => ({
        beforeUpload: (file) => {
            handleUpload(file);
            return false;
        },
        showUploadList: false,
        accept: '.csv',
        disabled: uploading,
    }), [handleUpload, uploading]);

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card
                styles={{
                    body: {
                        padding: 16
                    }
                }}
                style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}
            >
                <Flex
                    gap="middle"
                    wrap="wrap"
                    align="center"
                    justify="flex-end"
                    style={{ width: '100%' }}
                >
                    <Button onClick={downloadTemplate} icon={<FileTextOutlined />}>
                        {t('download_template')}
                    </Button>
                    <Upload {...uploadProps}>
                        <Button
                            type="primary"
                            icon={<UploadOutlined />}
                            loading={uploading}
                            disabled={uploading}
                        >
                            {uploading ? t('uploading') : t('import_data')}
                        </Button>
                    </Upload>
                </Flex>
            </Card>

            <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                <AppTable
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    scroll={{ x: 600 }}
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: !screens.xs,
                    }}
                    onChange={handleTableChange}
                    locale={{
                        emptyText: (
                            <div style={{ padding: '32px 0', textAlign: 'center' }}>
                                <HistoryOutlined style={{ fontSize: '32px', color: '#8c8c8c', marginBottom: '16px' }} />
                                <Typography.Title level={5} type="secondary">{t('no_historical_imports')}</Typography.Title>
                                <Typography.Text type="secondary">{t('upload_csv_description')}</Typography.Text>
                            </div>
                        )
                    }}
                />
            </Card>
        </Space>
    );
};

export default HistoricalImportsTab;
